import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Alert from "react-bootstrap/Alert";
import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Table from "react-bootstrap/Table";
import "./App.css";
import AppNavbar from "./AppNavbar.js";
import SectionCard from "./SectionCard.js";
import { communityFetch } from "./communityApi.js";
import {
  buildLoginUrl,
  buildLogoutUrl,
  buildSignupUrl,
  getTokenStorageKey,
  isTokenExpired,
  readTokenFromUrlHash,
} from "./auth.js";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
const COGNITO_DOMAIN = process.env.REACT_APP_COGNITO_DOMAIN || "";
const COGNITO_CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID || "";
const COGNITO_REDIRECT_URI = `${window.location.origin}/communities`;
const COGNITO_LOGOUT_URI = `${window.location.origin}/communities`;

function CommunityDetailPage() {
  const { communityId = "" } = useParams();
  const [idToken, setIdToken] = useState("");
  const [me, setMe] = useState(null);
  const [balance, setBalance] = useState({ cash_available: 0, cash_reserved: 0 });
  const [community, setCommunity] = useState(null);
  const [members, setMembers] = useState([]);
  const [joinRequests, setJoinRequests] = useState([]);
  const [joinPassword, setJoinPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState("");
  const [actionMessage, setActionMessage] = useState("");

  useEffect(() => {
    const tokenFromHash = readTokenFromUrlHash();
    const storedToken = localStorage.getItem(getTokenStorageKey()) || "";
    const token = tokenFromHash || storedToken;

    if (!token || isTokenExpired(token)) {
      localStorage.removeItem(getTokenStorageKey());
      setIdToken("");
      return;
    }

    localStorage.setItem(getTokenStorageKey(), token);
    setIdToken(token);
    if (tokenFromHash) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const loginUrl = useMemo(
    () => buildLoginUrl(COGNITO_DOMAIN, COGNITO_CLIENT_ID, COGNITO_REDIRECT_URI),
    []
  );
  const signupUrl = useMemo(
    () => buildSignupUrl(COGNITO_DOMAIN, COGNITO_CLIENT_ID, COGNITO_REDIRECT_URI),
    []
  );
  const logoutUrl = useMemo(
    () => buildLogoutUrl(COGNITO_DOMAIN, COGNITO_CLIENT_ID, COGNITO_LOGOUT_URI),
    []
  );

  const authFetch = useCallback(async (path) => {
    if (!API_BASE_URL) {
      throw new Error("Missing REACT_APP_API_BASE_URL");
    }
    if (!idToken) {
      throw new Error("Not signed in");
    }
    const response = await fetch(`${API_BASE_URL}${path}`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
    });
    const body = await response.json();
    if (!response.ok) {
      throw new Error(body.error || body.message || "Request failed");
    }
    return body;
  }, [idToken]);

  const loadPage = useCallback(async () => {
    if (!idToken || !communityId) {
      setCommunity(null);
      setMembers([]);
      setJoinRequests([]);
      setError("");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const [meRes, balanceRes, communityRes, membersRes] = await Promise.all([
        authFetch("/me"),
        authFetch("/me/balance"),
        communityFetch(`/communities/${communityId}`, idToken),
        communityFetch(`/communities/${communityId}/members`, idToken),
      ]);

      setMe(meRes);
      setBalance(balanceRes);
      setCommunity(communityRes.item || null);
      setMembers(Array.isArray(membersRes.items) ? membersRes.items : []);

      if (communityRes.item?.is_admin) {
        const requestsRes = await communityFetch(`/communities/${communityId}/join-requests`, idToken);
        setJoinRequests(Array.isArray(requestsRes.items) ? requestsRes.items : []);
      } else {
        setJoinRequests([]);
      }
    } catch (err) {
      setError(err.message || "Failed to load community");
    } finally {
      setLoading(false);
    }
  }, [authFetch, communityId, idToken]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  const handleLogin = () => {
    if (!loginUrl) {
      setError("Missing Cognito configuration in React env vars");
      return;
    }
    window.location.assign(loginUrl);
  };

  const handleSignup = () => {
    if (!signupUrl) {
      setError("Missing Cognito configuration in React env vars");
      return;
    }
    window.location.assign(signupUrl);
  };

  const handleLogout = () => {
    localStorage.removeItem(getTokenStorageKey());
    setIdToken("");
    setMe(null);
    setBalance({ cash_available: 0, cash_reserved: 0 });
    if (logoutUrl) {
      window.location.assign(logoutUrl);
    }
  };

  const runAction = useCallback(async (work, successMessage) => {
    setActionBusy(true);
    setError("");
    setActionMessage("");
    try {
      await work();
      setActionMessage(successMessage);
      await loadPage();
    } catch (err) {
      setError(err.message || "Request failed");
    } finally {
      setActionBusy(false);
    }
  }, [loadPage]);

  const handleJoin = async (event) => {
    event.preventDefault();
    await runAction(
      () => communityFetch(`/communities/${communityId}/join`, idToken, {
        method: "POST",
        body: JSON.stringify({
          password: joinPassword || undefined,
        }),
      }),
      "Join request submitted."
    );
    setJoinPassword("");
  };

  const handleLeave = async () => {
    await runAction(
      () => communityFetch(`/communities/${communityId}/leave`, idToken, {
        method: "POST",
      }),
      "You left the community."
    );
  };

  const handleJoinDecision = async (userId, action) => {
    await runAction(
      () => communityFetch(`/communities/${communityId}/join-requests/${userId}`, idToken, {
        method: "POST",
        body: JSON.stringify({ action }),
      }),
      `Join request ${action.toLowerCase()}d.`
    );
  };

  const handleRemoveMember = async (userId) => {
    await runAction(
      () => communityFetch(`/communities/${communityId}/members/${userId}`, idToken, {
        method: "DELETE",
      }),
      "Member removed."
    );
  };

  const handleAssignAdmin = async (userId) => {
    await runAction(
      () => communityFetch(`/communities/${communityId}/admins`, idToken, {
        method: "POST",
        body: JSON.stringify({ user_id: userId }),
      }),
      "Admin assigned."
    );
  };

  const canJoin = Boolean(
    community
    && !community.is_member
    && community.membership_status !== "PENDING"
  );
  const canLeave = Boolean(community?.is_member);

  return (
    <div className="App">
      <AppNavbar
        links={[
          { href: "/", label: "Home" },
          { href: "/open-orders", label: "Open Orders" },
          { href: "/communities", label: "Communities" },
          { href: "/communities/new", label: "Create Community" },
          { href: "/leaderboard", label: "Leaderboard" },
          { href: "/market-resolution", label: "Resolve Market" },
          { href: "/markets/new", label: "Create Market" },
        ]}
        isSignedIn={Boolean(idToken)}
        me={me}
        balance={balance}
        onLogin={handleLogin}
        onLogout={handleLogout}
        onSignup={handleSignup}
      />
      <Container className="py-4">
        {error && (
          <Row className="mb-3">
            <Col>
              <Alert variant="danger" className="mb-0">{error}</Alert>
            </Col>
          </Row>
        )}
        {actionMessage && (
          <Row className="mb-3">
            <Col>
              <Alert variant="success" className="mb-0">{actionMessage}</Alert>
            </Col>
          </Row>
        )}
        {!idToken ? (
          <Row>
            <Col>
              <Alert variant="info" className="mb-0">
                Sign in with Cognito to view community details.
              </Alert>
            </Col>
          </Row>
        ) : loading && !community ? (
          <Row>
            <Col>
              <Alert variant="secondary" className="mb-0">Loading community...</Alert>
            </Col>
          </Row>
        ) : (
          <>
            <Row className="g-4 mb-4">
              <Col lg={8}>
                <SectionCard
                  title={community?.name || communityId}
                  action={
                    <div className="community-badge-row">
                      {community?.visibility && (
                        <Badge bg={community.visibility === "PRIVATE" ? "warning" : "success"} text="dark">
                          {community.visibility}
                        </Badge>
                      )}
                      {community?.membership_status && (
                        <Badge bg="info" text="dark">
                          {community.membership_status}
                        </Badge>
                      )}
                    </div>
                  }
                >
                  <p className="community-detail-copy">
                    {community?.description || "No description yet."}
                  </p>
                  <div className="community-card-meta">
                    <span>Created by {community?.created_by_username || community?.created_by_user_id || "Unknown"}</span>
                    <span>
                      Markets stay hidden until membership is approved.
                    </span>
                  </div>
                </SectionCard>
              </Col>
              <Col lg={4}>
                <SectionCard title="Membership">
                  {canJoin ? (
                    <Form onSubmit={handleJoin}>
                      {community?.visibility === "PRIVATE" && (
                        <Form.Group className="mb-3 text-start">
                          <Form.Label>Password</Form.Label>
                          <Form.Control
                            type="password"
                            value={joinPassword}
                            onChange={(event) => setJoinPassword(event.target.value)}
                            disabled={actionBusy}
                            placeholder="Required for private communities"
                          />
                        </Form.Group>
                      )}
                      <Button type="submit" variant="light" disabled={actionBusy}>
                        Request to Join
                      </Button>
                    </Form>
                  ) : community?.membership_status === "PENDING" ? (
                    <Alert variant="warning" className="mb-0">
                      Your request is pending admin approval.
                    </Alert>
                  ) : canLeave ? (
                    <>
                      <Alert variant="success">
                        You are an approved {community?.is_admin ? "admin" : "member"}.
                      </Alert>
                      <Button variant="outline-light" onClick={handleLeave} disabled={actionBusy}>
                        Leave Community
                      </Button>
                    </>
                  ) : (
                    <Alert variant="secondary" className="mb-0">
                      You are not a member of this community.
                    </Alert>
                  )}
                </SectionCard>
              </Col>
            </Row>

            <Row className="g-4">
              <Col lg={community?.is_admin ? 7 : 12}>
                <SectionCard title="Members">
                  {members.length === 0 ? (
                    <Alert variant="secondary" className="mb-0">No approved members yet.</Alert>
                  ) : (
                    <div className="table-scroll-region">
                      <Table striped bordered hover responsive variant="dark">
                        <thead>
                          <tr>
                            <th>User</th>
                            <th>Role</th>
                            {community?.is_admin && <th>Actions</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {members.map((member) => (
                            <tr key={member.user_id}>
                              <td>{member.username || member.user_id}</td>
                              <td>{member.role}</td>
                              {community?.is_admin && (
                                <td className="community-action-cell">
                                  {member.role !== "ADMIN" && (
                                    <Button
                                      size="sm"
                                      variant="outline-info"
                                      disabled={actionBusy}
                                      onClick={() => handleAssignAdmin(member.user_id)}
                                    >
                                      Make Admin
                                    </Button>
                                  )}
                                  {member.user_id !== me?.sub && (
                                    <Button
                                      size="sm"
                                      variant="outline-danger"
                                      disabled={actionBusy}
                                      onClick={() => handleRemoveMember(member.user_id)}
                                    >
                                      Remove
                                    </Button>
                                  )}
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </SectionCard>
              </Col>

              {community?.is_admin && (
                <Col lg={5}>
                  <SectionCard title="Pending Requests">
                    {joinRequests.length === 0 ? (
                      <Alert variant="secondary" className="mb-0">No pending requests.</Alert>
                    ) : (
                      <div className="community-request-list">
                        {joinRequests.map((request) => (
                          <div key={request.user_id} className="community-request-card">
                            <div>
                              <strong>{request.username || request.user_id}</strong>
                              <div className="community-request-subtitle">{request.user_id}</div>
                            </div>
                            <div className="community-request-actions">
                              <Button
                                size="sm"
                                variant="success"
                                disabled={actionBusy}
                                onClick={() => handleJoinDecision(request.user_id, "APPROVE")}
                              >
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                disabled={actionBusy}
                                onClick={() => handleJoinDecision(request.user_id, "REJECT")}
                              >
                                Reject
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </SectionCard>
                </Col>
              )}
            </Row>
          </>
        )}
      </Container>
    </div>
  );
}

export default CommunityDetailPage;
